// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import {
  getAccountTransactions,
  computeRunningBalance,
  formatAmount,
  buildEvent,
  getRegisterColumnKeys,
} from '../../lib/eventLog.js';
import { getExchangeRate, convertCurrency } from '../../lib/currency-math.js';
import { getCurrencyDecimalPlaces } from '../../lib/currencies.js';
import { formatDate } from '../../lib/formatLocale.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { TransactionForm } from './TransactionForm.js';
import { ReconcileView } from '../accounts/ReconcileView.js';
import { ReconcileMark } from '../ui/ReconcileMark.js';
import { InvestmentRegister } from '../investment/InvestmentRegister.js';
import { INVESTMENT_ACCOUNT_TYPES } from '../../lib/investment.js';
import type { Transaction, TransactionVoidedPayload, TransactionDeletedPayload } from '../../types.js';

interface TransactionRegisterProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

export function TransactionRegister({ worker, keyData }: TransactionRegisterProps) {
  const { t } = useTranslation(['transactions', 'accounting', 'common', 'errors']);
  const { state, dispatch } = useApp();
  const { ledger } = state;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const [showForm, setShowForm] = useState(false);
  const [editingTx, setEditingTx] = useState<Transaction | undefined>();
  const [showReconcile, setShowReconcile] = useState(false);
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);
  const [expandedTxId, setExpandedTxId] = useState<string | null>(null);
  const [sortDesc, setSortDesc] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ txId: string; x: number; y: number } | null>(null);

  const [voidingTxId, setVoidingTxId] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState('');
  const [voiding, setVoiding] = useState(false);

  useEffect(() => {
    if (id) dispatch({ type: 'ACCOUNT_VIEWED', accountId: id });
  }, [id, dispatch]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'n' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setShowForm(true);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Hooks must run unconditionally, before any early return below.
  const allPrices = useMemo(() => [...ledger.prices.values()], [ledger.prices]);

  if (!id) {
    return (
      <div className="flex-1 flex items-center justify-center text-text-muted">
        <p>{t('register.selectAccountPrompt')}</p>
      </div>
    );
  }

  const account = ledger.accounts.get(id);
  if (!account) {
    return (
      <div className="flex-1 flex items-center justify-center text-text-muted">
        <p>{t('register.accountNotFound')}</p>
      </div>
    );
  }

  // DECISION: Investment accounts (STOCK, MUTUAL_FUND) use a dedicated register
  // that shows share quantities and actions instead of debit/credit columns.
  if (INVESTMENT_ACCOUNT_TYPES.has(account.type)) {
    return <InvestmentRegister account={account} worker={worker} keyData={keyData} />;
  }

  const allTransactions = getAccountTransactions(ledger, id);
  const displayTransactions = sortDesc ? [...allTransactions].reverse() : allTransactions;
  const withBalance = computeRunningBalance(
    sortDesc ? [...allTransactions] : allTransactions,
    id,
  );
  const balanceMap = new Map(withBalance.map(({ tx, runningBalance }) => [tx.id, runningBalance]));

  const cols = getRegisterColumnKeys(account.type) as {
    debit: 'withdrawal' | 'deposit' | 'charge' | 'payment' | 'income' | 'expense' | 'rebate' | 'decrease' | 'increase';
    credit: 'withdrawal' | 'deposit' | 'charge' | 'payment' | 'income' | 'expense' | 'rebate' | 'decrease' | 'increase';
  };
  const debitLabel = t(`accounting:registerColumns.${cols.debit}`) as string;
  const creditLabel = t(`accounting:registerColumns.${cols.credit}`) as string;

  const homeCurrency = ledger.settings.homeCurrency;
  const isForeignAccount = account.currency !== homeCurrency;
  const today = new Date().toISOString().slice(0, 10);
  const homeRate = isForeignAccount
    ? getExchangeRate(account.currency, homeCurrency, today, allPrices)
    : null;
  const homeBalance = homeRate !== null
    ? convertCurrency(account.balance, account.currency, homeCurrency, homeRate, getCurrencyDecimalPlaces(account.currency), getCurrencyDecimalPlaces(homeCurrency))
    : null;

  const unreconciledCount = [...ledger.transactions.values()].reduce((n, tx) => {
    if (tx.voided) return n;
    return n + tx.splits.filter((s) => s.accountId === id && s.reconcileState !== 'y').length;
  }, 0);

  function getSplitForAccount(tx: Transaction) {
    return tx.splits.find((s) => s.accountId === id);
  }

  function getTransferLabel(tx: Transaction): string {
    const otherSplits = tx.splits.filter((s) => s.accountId !== id);
    if (otherSplits.length === 0) return '—';
    if (otherSplits.length > 1) return `— ${t('card.split')} —`;
    const acc = ledger.accounts.get(otherSplits[0]!.accountId);
    return acc?.name ?? otherSplits[0]!.accountId;
  }

  async function handleVoid(txId: string) {
    setVoidError('');
    setVoiding(true);
    try {
      const payload: TransactionVoidedPayload = {
        transactionId: txId,
        voidReason: voidReason.trim() || t('register.voidedByUser'),
      };
      await encryptAndPush(await buildEvent('TransactionVoided', payload, ledger.lastSequence + 1));
      setVoidingTxId(null);
      setVoidReason('');
      setExpandedTxId(null);
    } catch (err) {
      setVoidError(err instanceof Error ? err.message : t('register.voidFailed'));
    } finally {
      setVoiding(false);
    }
  }

  async function handleDelete(txId: string) {
    if (!confirm(t('register.confirmDelete'))) return;
    try {
      const payload: TransactionDeletedPayload = { transactionId: txId };
      await encryptAndPush(await buildEvent('TransactionDeleted', payload, ledger.lastSequence + 1));
      setSelectedTxId(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : t('errors:deleteTransaction'));
    }
  }

  async function handleDuplicate(tx: Transaction) {
    const splits = tx.splits.map((s) => ({
      ...s,
      splitId: crypto.randomUUID(),
      reconcileState: 'n' as const,
      reconcileDate: null,
    }));
    const payload = {
      transactionId: crypto.randomUUID(),
      date: new Date().toISOString().slice(0, 10),
      dateEntered: new Date().toISOString(),
      description: tx.description,
      num: tx.num,
      notes: tx.notes,
      currency: tx.currency,
      splits,
    };
    try {
      await encryptAndPush(await buildEvent('TransactionPosted', payload, ledger.lastSequence + 1));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('errors:duplicate'));
    }
  }

  async function handleAddReversing(tx: Transaction) {
    const reversedSplits = tx.splits.map((s) => ({
      ...s,
      splitId: crypto.randomUUID(),
      amount: -s.amount,
      quantity: -s.quantity,
      reconcileState: 'n' as const,
      reconcileDate: null,
    }));
    const payload = {
      transactionId: crypto.randomUUID(),
      date: new Date().toISOString().slice(0, 10),
      dateEntered: new Date().toISOString(),
      description: t('register.reversingDescription', { description: tx.description }),
      num: '',
      notes: t('register.reversesFrom', { date: tx.date }),
      currency: tx.currency,
      splits: reversedSplits,
    };
    try {
      await encryptAndPush(await buildEvent('TransactionPosted', payload, ledger.lastSequence + 1));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('errors:addReversing'));
    }
  }

  function handleContextMenu(e: React.MouseEvent, txId: string) {
    e.preventDefault();
    setContextMenu({ txId, x: e.clientX, y: e.clientY });
  }

  function closeContextMenu() {
    setContextMenu(null);
  }

  // Compute cleared and reconciled balances for status bar
  const clearedBalance = allTransactions.reduce((sum, tx) => {
    if (tx.voided) return sum;
    const split = tx.splits.find((s) => s.accountId === id);
    if (split && (split.reconcileState === 'c' || split.reconcileState === 'y')) {
      return sum + split.amount;
    }
    return sum;
  }, 0);

  const reconciledBalance = allTransactions.reduce((sum, tx) => {
    if (tx.voided) return sum;
    const split = tx.splits.find((s) => s.accountId === id);
    if (split && split.reconcileState === 'y') return sum + split.amount;
    return sum;
  }, 0);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" onClick={closeContextMenu}>
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between bg-bg-surface shrink-0">
        <div className="flex items-baseline gap-3">
          <h2 className="text-md font-semibold text-text">{account.name}</h2>
          <span className={`text-sm font-mono font-medium ${account.balance < 0 ? 'text-negative' : 'text-text'}`}>
            {formatAmount(account.balance, account.currency)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSortDesc(!sortDesc)}
            className="px-2 py-1 text-xs border border-border text-text-muted hover:bg-bg-elevated hover:text-text transition-colors"
            title={t('register.toggleSortOrder')}
          >
            {sortDesc ? `↑ ${t('register.oldest')}` : `↓ ${t('register.newest')}`}
          </button>
          {!account.placeholder && (
            <button
              onClick={() => setShowReconcile(true)}
              className="px-3 py-1 border border-border text-text-muted text-sm hover:bg-bg-elevated hover:text-text transition-colors"
            >
              {t('register.reconcile')}
              {unreconciledCount > 0 && (
                <span className="ml-1.5 text-xs text-warning">({unreconciledCount})</span>
              )}
            </button>
          )}
          {!account.placeholder && (
            <button
              onClick={() => navigate(`/import?accountId=${id}`)}
              className="px-3 py-1 border border-border text-text-muted text-sm hover:bg-bg-elevated hover:text-text transition-colors"
            >
              {t('register.import')}
            </button>
          )}
          {!account.placeholder && (
            <button
              onClick={() => { setEditingTx(undefined); setShowForm(true); }}
              className="px-3 py-1 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
            >
              {t('common:actions.new')}
            </button>
          )}
        </div>
      </div>

      {/* Register table */}
      <div className="flex-1 overflow-auto">
        {account.placeholder ? (
          <div className="flex items-center justify-center h-32 text-text-muted">
            <p className="text-sm">{t('register.placeholderNotAllowed')}</p>
          </div>
        ) : displayTransactions.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-text-muted">
            <div className="text-center">
              <p className="text-sm">{t('register.noTransactions')}</p>
              <button onClick={() => setShowForm(true)} className="mt-1 text-accent hover:text-accent-hover text-sm">
                {t('register.addFirst')}
              </button>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="bg-bg-surface border-b border-border-strong sticky top-0 z-10">
              <tr>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 90 }}>{t('accounting:columns.date')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 60 }}>{t('accounting:columns.num')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2">{t('accounting:columns.description')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 140 }}>{t('accounting:columns.transfer')}</th>
                <th className="text-center text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-1" style={{ width: 28 }}>{t('accounting:columns.reconcileShort')}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 90 }}>{debitLabel}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 90 }}>{creditLabel}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-3" style={{ width: 100 }}>{t('accounting:columns.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {displayTransactions.map((tx, idx) => {
                const split = getSplitForAccount(tx);
                const amount = split?.amount ?? 0;
                const isExpanded = expandedTxId === tx.id;
                const isSelected = selectedTxId === tx.id;
                const isVoiding = voidingTxId === tx.id;
                const runningBalance = balanceMap.get(tx.id) ?? 0;
                const isDebit = amount > 0;
                const isCredit = amount < 0;
                const rowBg = isSelected
                  ? 'bg-bg-elevated border-l-2 border-accent'
                  : idx % 2 === 0
                  ? 'bg-bg border-l-2 border-transparent'
                  : 'bg-bg-surface border-l-2 border-transparent';

                return (
                  <React.Fragment key={tx.id}>
                    <tr
                      className={`border-b border-border cursor-pointer transition-colors hover:bg-bg-elevated ${rowBg} ${tx.voided ? 'opacity-40' : ''}`}
                      style={{ height: 28 }}
                      onClick={() => {
                        setSelectedTxId(tx.id);
                        setExpandedTxId(isExpanded ? null : tx.id);
                        if (voidingTxId !== tx.id) { setVoidingTxId(null); setVoidReason(''); }
                      }}
                      onContextMenu={(e) => handleContextMenu(e, tx.id)}
                    >
                      <td className="py-0.5 px-2 text-text-muted font-mono text-xs whitespace-nowrap">{formatDate(tx.date, { month: '2-digit', day: '2-digit', year: '2-digit' })}</td>
                      <td className="py-0.5 px-2 text-text-subtle font-mono text-xs">{tx.num}</td>
                      <td className="py-0.5 px-2">
                        <span className={`text-text ${tx.voided ? 'line-through' : ''}`}>{tx.description}</span>
                        {tx.notes && <span className="text-xs text-text-subtle ml-2">{tx.notes}</span>}
                        {tx.voided && <span className="text-xs text-negative ml-2">{t('register.voidBadge')}</span>}
                      </td>
                      <td className="py-0.5 px-2 text-text-subtle text-xs truncate" style={{ maxWidth: 140 }}>{getTransferLabel(tx)}</td>
                      <td className="py-0.5 px-1 text-center">
                        <ReconcileMark state={split?.reconcileState ?? 'n'} size="sm" />
                      </td>
                      <td className="py-0.5 px-2 text-right font-mono text-text">
                        {isDebit && !tx.voided ? formatAmount(amount, account.currency) : ''}
                      </td>
                      <td className="py-0.5 px-2 text-right font-mono text-text">
                        {isCredit && !tx.voided ? formatAmount(Math.abs(amount), account.currency) : ''}
                      </td>
                      <td className="py-0.5 px-3 text-right font-mono text-text-muted">
                        {formatAmount(runningBalance, account.currency)}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="bg-bg-elevated px-6 pb-3 pt-2 border-b border-border">
                          {/* Split detail */}
                          <div className="text-xs">
                            <p className="font-medium text-text-muted mb-1.5 uppercase tracking-wide text-xs">{t('form.splits')}</p>
                            <table className="w-full">
                              <thead>
                                <tr className="text-text-subtle text-xs border-b border-border">
                                  <th className="text-left pb-1 font-medium">{t('accounting:columns.account')}</th>
                                  <th className="text-left pb-1 font-medium">{t('form.memo')}</th>
                                  <th className="text-center pb-1 font-medium">{t('accounting:columns.reconcileShort')}</th>
                                  <th className="text-right pb-1 font-medium">{debitLabel}</th>
                                  <th className="text-right pb-1 font-medium">{creditLabel}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tx.splits.map((s) => {
                                  const acc = ledger.accounts.get(s.accountId);
                                  const splitCur = acc?.currency ?? account.currency;
                                  return (
                                    <tr key={s.splitId} className="border-b border-border">
                                      <td className="py-1 pr-3">
                                        <button
                                          className="text-accent hover:text-accent-hover text-xs"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (s.accountId !== id) navigate(`/accounts/${s.accountId}`);
                                          }}
                                        >
                                          {acc?.name ?? s.accountId}
                                        </button>
                                        {splitCur !== account.currency && (
                                          <span className="ml-1 text-xs font-mono text-text-subtle">{splitCur}</span>
                                        )}
                                      </td>
                                      <td className="py-1 pr-3 text-text-subtle">{s.memo}</td>
                                      <td className="py-1 px-2 text-center">
                                        <ReconcileMark state={s.reconcileState} size="sm" />
                                      </td>
                                      <td className="py-1 text-right font-mono text-text">
                                        {s.amount > 0 ? formatAmount(s.amount, splitCur) : ''}
                                      </td>
                                      <td className="py-1 text-right font-mono text-text">
                                        {s.amount < 0 ? formatAmount(Math.abs(s.amount), splitCur) : ''}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Actions */}
                          {!tx.voided && (
                            <div className="mt-2 pt-2 border-t border-border flex items-center gap-4 flex-wrap">
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingTx(tx); setShowForm(true); }}
                                className="text-xs text-accent hover:text-accent-hover"
                              >
                                {t('common:actions.edit')}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); void handleDuplicate(tx); }}
                                className="text-xs text-text-muted hover:text-text"
                              >
                                {t('common:actions.duplicate')}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); void handleAddReversing(tx); }}
                                className="text-xs text-text-muted hover:text-text"
                              >
                                {t('register.addReversing')}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); void handleDelete(tx.id); }}
                                className="text-xs text-negative hover:opacity-80 ml-auto"
                              >
                                {t('common:actions.delete')}
                              </button>
                              {isVoiding ? (
                                <div className="w-full flex items-center gap-2 mt-1">
                                  <input
                                    type="text"
                                    value={voidReason}
                                    onChange={(e) => setVoidReason(e.target.value)}
                                    placeholder={t('register.voidReasonPlaceholder')}
                                    className="flex-1 bg-bg border border-border text-text px-2 py-1 text-xs"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <button
                                    onClick={(e) => { e.stopPropagation(); void handleVoid(tx.id); }}
                                    disabled={voiding}
                                    className="px-2 py-1 bg-negative text-white text-xs font-medium hover:opacity-80 disabled:opacity-50"
                                  >
                                    {voiding ? t('register.voiding') : t('register.confirmVoid')}
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setVoidingTxId(null); setVoidReason(''); }}
                                    className="px-2 py-1 border border-border text-xs text-text-muted hover:bg-bg-elevated"
                                  >
                                    {t('common:actions.cancel')}
                                  </button>
                                  {voidError && <span className="text-xs text-negative">{voidError}</span>}
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setVoidingTxId(tx.id); setVoidReason(''); setVoidError(''); }}
                                  className="text-xs text-negative hover:opacity-80"
                                >
                                  {t('register.void')}
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Status bar */}
      {!account.placeholder && displayTransactions.length > 0 && (
        <div className="px-4 py-1.5 border-t border-border bg-bg-surface flex items-center gap-6 shrink-0 text-xs text-text-muted">
          <span>{t('register.cleared')} <span className="font-mono text-text">{formatAmount(clearedBalance, account.currency)}</span></span>
          <span>{t('register.reconciledLabel')} <span className="font-mono text-positive">{formatAmount(reconciledBalance, account.currency)}</span></span>
          <span className="ml-auto">
            {t('register.balanceLabel')} <span className="font-mono text-text font-medium">{formatAmount(account.balance, account.currency)}</span>
            {isForeignAccount && homeBalance !== null && (
              <span className="ml-1 text-text-subtle">
                (≈ {formatAmount(homeBalance, homeCurrency)})
              </span>
            )}
            {isForeignAccount && homeBalance === null && (
              <span className="ml-1 text-text-subtle">{t('register.noRate')}</span>
            )}
          </span>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (() => {
        const tx = ledger.transactions.get(contextMenu.txId);
        if (!tx) return null;
        return (
          <div
            className="fixed z-50 bg-bg-surface border border-border py-1 text-sm shadow-xl"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="w-full text-left px-4 py-1.5 text-text-muted hover:bg-bg-elevated hover:text-text"
              onClick={() => { closeContextMenu(); setEditingTx(tx); setShowForm(true); }}>
              {t('common:actions.edit')}
            </button>
            <button className="w-full text-left px-4 py-1.5 text-text-muted hover:bg-bg-elevated hover:text-text"
              onClick={() => { closeContextMenu(); void handleDuplicate(tx); }}>
              {t('common:actions.duplicate')}
            </button>
            <button className="w-full text-left px-4 py-1.5 text-text-muted hover:bg-bg-elevated hover:text-text"
              onClick={() => { closeContextMenu(); void handleAddReversing(tx); }}>
              {t('register.addReversingEntry')}
            </button>
            <div className="border-t border-border my-0.5" />
            {!tx.voided && (
              <button className="w-full text-left px-4 py-1.5 text-warning hover:bg-bg-elevated"
                onClick={() => { closeContextMenu(); setExpandedTxId(tx.id); setVoidingTxId(tx.id); }}>
                {t('register.void')}
              </button>
            )}
            <button className="w-full text-left px-4 py-1.5 text-negative hover:bg-bg-elevated"
              onClick={() => { closeContextMenu(); void handleDelete(tx.id); }}>
              {t('common:actions.delete')}
            </button>
          </div>
        );
      })()}

      {showForm && (
        <TransactionForm
          defaultAccountId={id}
          {...(editingTx !== undefined ? { editTransaction: editingTx } : {})}
          worker={worker}
          keyData={keyData}
          onClose={() => { setShowForm(false); setEditingTx(undefined); }}
        />
      )}

      {showReconcile && (
        <ReconcileView
          accountId={id}
          worker={worker}
          keyData={keyData}
          onClose={() => setShowReconcile(false)}
        />
      )}
    </div>
  );
}
